import { useState, useEffect } from "react";
import {
  ArrowLeft,
  ChevronRight,
  Plus as PlusIcon,
  Minus,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import { showError, showSuccess, showSeoWarnings } from "../lib/toast";
import {
  server_post_data,
  case_study_save_update,
  get_case_study_data,
} from "../serviceconnection/ServiceConnection";
import { MediaImageField } from "../components/MediaPickerDialog";

// ─────────────────────────────────────────────────────────────────────────────
const TABS = ["basic", "details", "seo"];

const EMPTY_FORM = {
  p_id: 0,
  title: "",
  industry: "",
  solution: "",
  hero_image: "",
  summary: "",
  client_background: "",
  timeline: "",
  team_size: "",
  status: 1,
  results: [{ metric: "", label: "" }],
  challenges: [""],
  solution_implemented: { overview: "", keyComponents: [""] },
  outcomes: { description: "", benefits: [""] },
  testimonial: { quote: "", author: "", role: "" },
  seo_id: 0,
  actual_url: "",
  link_url: "",
  canonical_url: "",
  title_name: "",
  description: "",
  keywords: "",
  pretty_function_name: "",
  call_function_name: "",
  content_type: "article",
  changefreq: "monthly",
  priority_screen: "0.8",
};

// ── Defined OUTSIDE component to prevent remount on every render ──────────────
const InputField = ({
  label,
  name,
  placeholder,
  required,
  textarea,
  value,
  form = {},
  setField = () => {},
  onChange,
}) => (
  <div className="space-y-1">
    <label className="text-xs font-semibold text-muted-foreground">
      {label}
      {required && <span className="text-destructive"> *</span>}
    </label>
    {textarea ? (
      <textarea
        name={name}
        value={value ?? form[name]}
        onChange={onChange ?? ((e) => setField(name, e.target.value))}
        rows={4}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent resize-none"
      />
    ) : (
      <input
        name={name}
        value={value ?? form[name]}
        onChange={onChange ?? ((e) => setField(name, e.target.value))}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
      />
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
export default function CaseStudyForm({ editId, onBack, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [activeTab, setActiveTab] = useState("basic");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        setFetching(true);
        const fd = new FormData();
        fd.append("main_id", editId);
        const res = await server_post_data(get_case_study_data, fd);
        const d = res.data?.data;
        if (!d) {
          showError("Failed to load case study");
          return;
        }
        const seo = d.seo_data ?? {};
        setForm({
          p_id: d.primary_id,
          title: d.title ?? "",
          industry: d.industry ?? "",
          solution: d.solution ?? "",
          hero_image: d.hero_image ?? "",
          summary: d.summary ?? "",
          client_background: d.client_background ?? "",
          timeline: d.timeline ?? "",
          team_size: d.team_size ?? "",
          status: d.status ?? 1,
          results: d.results?.length ? d.results : [{ metric: "", label: "" }],
          challenges: d.challenges?.length ? d.challenges : [""],
          solution_implemented:
            d.solution_implemented?.overview !== undefined
              ? d.solution_implemented
              : { overview: "", keyComponents: [""] },
          outcomes:
            d.outcomes?.description !== undefined
              ? d.outcomes
              : { description: "", benefits: [""] },
          testimonial:
            d.testimonial?.quote !== undefined
              ? d.testimonial
              : { quote: "", author: "", role: "" },
          seo_id: d.seo_id ?? 0,
          actual_url: seo.actual_url ?? "",
          link_url: seo.link_url ?? "",
          canonical_url: seo.canonical_url ?? "",
          title_name: seo.title_name ?? "",
          description: seo.description ?? "",
          keywords: seo.keywords ?? "",
          pretty_function_name: seo.pretty_function_name ?? "",
          call_function_name: seo.call_function_name ?? "",
          content_type: seo.content_type ?? "article",
          changefreq: seo.changefreq ?? "monthly",
          priority_screen: seo.priority_screen ?? "0.8",
        });
      } catch {
        showError("Failed to load case study");
      } finally {
        setFetching(false);
      }
    })();
  }, [editId]);

  const setField = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const updateArrItem = (key, idx, val) =>
    setField(
      key,
      form[key].map((item, i) => (i === idx ? val : item)),
    );
  const addArrItem = (key, empty) => setField(key, [...form[key], empty]);
  const removeArrItem = (key, idx) =>
    setField(
      key,
      form[key].filter((_, i) => i !== idx),
    );

  const updateNestedArr = (objKey, arrKey, idx, val) =>
    setField(objKey, {
      ...form[objKey],
      [arrKey]: form[objKey][arrKey].map((item, i) => (i === idx ? val : item)),
    });
  const addNestedArr = (objKey, arrKey) =>
    setField(objKey, {
      ...form[objKey],
      [arrKey]: [...form[objKey][arrKey], ""],
    });
  const removeNestedArr = (objKey, arrKey, idx) =>
    setField(objKey, {
      ...form[objKey],
      [arrKey]: form[objKey][arrKey].filter((_, i) => i !== idx),
    });

  const handleSave = async () => {
    if (
      !form.title ||
      !form.industry ||
      !form.solution ||
      !form.summary ||
      !form.client_background
    ) {
      showError(
        "Please fill all required fields (Title, Industry, Solution, Summary, Client Background)",
      );
      return;
    }
    try {
      setLoading(true);
      const fd = new FormData();
      [
        "p_id",
        "title",
        "industry",
        "solution",
        "hero_image",
        "summary",
        "client_background",
        "timeline",
        "team_size",
        "status",
        "seo_id",
        "actual_url",
        "link_url",
        "canonical_url",
        "title_name",
        "description",
        "keywords",
        "pretty_function_name",
        "call_function_name",
        "content_type",
        "changefreq",
        "priority_screen",
      ].forEach((k) => fd.append(k, form[k] ?? ""));
      fd.append(
        "results",
        JSON.stringify(form.results.filter((r) => r.metric)),
      );
      fd.append("challenges", JSON.stringify(form.challenges.filter(Boolean)));
      fd.append(
        "solution_implemented",
        JSON.stringify(form.solution_implemented),
      );
      fd.append("outcomes", JSON.stringify(form.outcomes));
      fd.append("testimonial", JSON.stringify(form.testimonial));

      const res = await server_post_data(case_study_save_update, fd);
      if (res.data?.error) {
        showError(res.data?.message || "Save failed");
        return;
      }
      showSuccess(
        form.p_id > 0 ? "Case study updated!" : "Case study created!",
      );
      showSeoWarnings(res);
      onSaved();
    } catch {
      showError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="max-w-[900px] mx-auto space-y-5">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="page-title">Loading...</h1>
        </div>
        <div className="section-panel p-10 text-center text-muted-foreground text-sm">
          Fetching case study data...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[900px] mx-auto space-y-5">
      {/* ── Page Header ── */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-muted border border-border text-muted-foreground"
            title="Back to list"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="page-title">
              {form.p_id > 0 ? "Edit Case Study" : "Add Case Study"}
            </h1>
            <p className="page-subtitle">
              {form.p_id > 0
                ? `Editing: ${form.title || "Untitled"}`
                : "Fill in the details to create a new case study"}
            </p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={loading}
          className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-60"
          style={{ background: "hsl(var(--accent))" }}
        >
          {loading ? "Saving..." : form.p_id > 0 ? "Update" : "Create"}
        </button>
      </div>

      {/* ── Tabs ── */}
      <div className="section-panel p-0">
        <div className="flex border-b border-border px-5">
          {[
            { key: "basic", label: "Basic Info" },
            { key: "details", label: "Content" },
            { key: "seo", label: "SEO Settings" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-3 text-xs font-semibold border-b-2 transition-colors -mb-px ${
                activeTab === t.key
                  ? "border-accent text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="section-panel-body space-y-5">
          {/* ══ BASIC INFO ══ */}
          {activeTab === "basic" && (
            <>
              <InputField
                label="Title"
                name="title"
                placeholder="e.g. How we helped XYZ grow 50%"
                required
                form={form}
                setField={setField}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField
                  label="Industry"
                  name="industry"
                  placeholder="e.g. Food & Beverage"
                  required
                  form={form}
                  setField={setField}
                />
                <InputField
                  label="Solution"
                  name="solution"
                  placeholder="e.g. SAP Business One"
                  required
                  form={form}
                  setField={setField}
                />
              </div>

              {/* Hero Image */}
              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted-foreground">
                  Hero Image
                </label>
                <MediaImageField
                  label="Select hero image"
                  hint="Recommended: 1200×630px"
                  value={form.hero_image}
                  onChange={(v) => setField("hero_image", v)}
                />
              </div>

              <InputField
                label="Summary"
                name="summary"
                placeholder="Brief overview shown on listing page..."
                required
                textarea
                form={form}
                setField={setField}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField
                  label="Timeline"
                  name="timeline"
                  placeholder="e.g. 6 Months"
                  form={form}
                  setField={setField}
                />
                <InputField
                  label="Team Size"
                  name="team_size"
                  placeholder="e.g. 8 Members"
                  form={form}
                  setField={setField}
                />
              </div>

              {/* Key Results */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-muted-foreground">
                  Key Results / Metrics
                </label>
                {form.results.map((r, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input
                      value={r.metric}
                      onChange={(e) =>
                        updateArrItem("results", i, {
                          ...r,
                          metric: e.target.value,
                        })
                      }
                      placeholder="50%"
                      className="w-24 px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                    />
                    <input
                      value={r.label}
                      onChange={(e) =>
                        updateArrItem("results", i, {
                          ...r,
                          label: e.target.value,
                        })
                      }
                      placeholder="Revenue Growth"
                      className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                    />
                    {form.results.length > 1 && (
                      <button
                        onClick={() => removeArrItem("results", i)}
                        className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted"
                        style={{ color: "hsl(var(--destructive))" }}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() =>
                    addArrItem("results", { metric: "", label: "" })
                  }
                  className="flex items-center gap-1 text-xs font-semibold"
                  style={{ color: "hsl(var(--accent))" }}
                >
                  <PlusIcon className="w-3 h-3" /> Add Metric
                </button>
              </div>

              {/* Show on Website toggle */}
              <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/30">
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    Show on Website
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Toggle visibility on public page
                  </p>
                </div>
                <button
                  onClick={() => setField("status", form.status === 1 ? 0 : 1)}
                  style={{
                    color:
                      form.status === 1
                        ? "hsl(var(--success))"
                        : "hsl(var(--muted-foreground))",
                  }}
                >
                  {form.status === 1 ? (
                    <ToggleRight className="w-8 h-8" />
                  ) : (
                    <ToggleLeft className="w-8 h-8" />
                  )}
                </button>
              </div>
            </>
          )}

          {/* ══ CONTENT ══ */}
          {activeTab === "details" && (
            <>
              <InputField
                label="Client Background"
                name="client_background"
                placeholder="Tell about the client, their business..."
                required
                textarea
                form={form}
                setField={setField}
              />

              {/* Challenges */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-muted-foreground">
                  Challenges
                </label>
                {form.challenges.map((ch, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input
                      value={ch}
                      onChange={(e) =>
                        updateArrItem("challenges", i, e.target.value)
                      }
                      placeholder={`Challenge ${i + 1}`}
                      className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                    />
                    {form.challenges.length > 1 && (
                      <button
                        onClick={() => removeArrItem("challenges", i)}
                        className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted"
                        style={{ color: "hsl(var(--destructive))" }}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => addArrItem("challenges", "")}
                  className="flex items-center gap-1 text-xs font-semibold"
                  style={{ color: "hsl(var(--accent))" }}
                >
                  <PlusIcon className="w-3 h-3" /> Add Challenge
                </button>
              </div>

              {/* Solution Implemented */}
              <div className="space-y-3 p-4 rounded-lg border border-border bg-muted/20">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Solution Implemented
                </p>
                <InputField
                  label="Overview"
                  name="sol_overview"
                  placeholder="Describe the solution..."
                  value={form.solution_implemented.overview}
                  onChange={(e) =>
                    setField("solution_implemented", {
                      ...form.solution_implemented,
                      overview: e.target.value,
                    })
                  }
                  textarea
                />
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-muted-foreground">
                    Key Components
                  </label>
                  {form.solution_implemented.keyComponents.map((kc, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input
                        value={kc}
                        onChange={(e) =>
                          updateNestedArr(
                            "solution_implemented",
                            "keyComponents",
                            i,
                            e.target.value,
                          )
                        }
                        placeholder={`Component ${i + 1}`}
                        className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                      />
                      {form.solution_implemented.keyComponents.length > 1 && (
                        <button
                          onClick={() =>
                            removeNestedArr(
                              "solution_implemented",
                              "keyComponents",
                              i,
                            )
                          }
                          className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted"
                          style={{ color: "hsl(var(--destructive))" }}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() =>
                      addNestedArr("solution_implemented", "keyComponents")
                    }
                    className="flex items-center gap-1 text-xs font-semibold"
                    style={{ color: "hsl(var(--accent))" }}
                  >
                    <PlusIcon className="w-3 h-3" /> Add Component
                  </button>
                </div>
              </div>

              {/* Outcomes */}
              <div className="space-y-3 p-4 rounded-lg border border-border bg-muted/20">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Outcomes
                </p>
                <InputField
                  label="Description"
                  name="out_desc"
                  placeholder="Describe the outcomes..."
                  value={form.outcomes.description}
                  onChange={(e) =>
                    setField("outcomes", {
                      ...form.outcomes,
                      description: e.target.value,
                    })
                  }
                  textarea
                />
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-muted-foreground">
                    Benefits
                  </label>
                  {form.outcomes.benefits.map((b, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input
                        value={b}
                        onChange={(e) =>
                          updateNestedArr(
                            "outcomes",
                            "benefits",
                            i,
                            e.target.value,
                          )
                        }
                        placeholder={`Benefit ${i + 1}`}
                        className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                      />
                      {form.outcomes.benefits.length > 1 && (
                        <button
                          onClick={() =>
                            removeNestedArr("outcomes", "benefits", i)
                          }
                          className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted"
                          style={{ color: "hsl(var(--destructive))" }}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => addNestedArr("outcomes", "benefits")}
                    className="flex items-center gap-1 text-xs font-semibold"
                    style={{ color: "hsl(var(--accent))" }}
                  >
                    <PlusIcon className="w-3 h-3" /> Add Benefit
                  </button>
                </div>
              </div>

              {/* Testimonial */}
              <div className="space-y-3 p-4 rounded-lg border border-border bg-muted/20">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Client Testimonial
                </p>
                <InputField
                  label="Quote"
                  name="test_quote"
                  placeholder="What the client said..."
                  value={form.testimonial.quote}
                  onChange={(e) =>
                    setField("testimonial", {
                      ...form.testimonial,
                      quote: e.target.value,
                    })
                  }
                  textarea
                />
                <div className="grid grid-cols-2 gap-3">
                  <InputField
                    label="Author"
                    name="test_author"
                    placeholder="e.g. John Smith"
                    value={form.testimonial.author}
                    onChange={(e) =>
                      setField("testimonial", {
                        ...form.testimonial,
                        author: e.target.value,
                      })
                    }
                  />
                  <InputField
                    label="Role"
                    name="test_role"
                    placeholder="e.g. CEO, XYZ Company"
                    value={form.testimonial.role}
                    onChange={(e) =>
                      setField("testimonial", {
                        ...form.testimonial,
                        role: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </>
          )}

          {/* ══ SEO ══ */}
          {activeTab === "seo" && (
            <>
              <p className="text-xs text-muted-foreground p-3 rounded-lg bg-muted/40 border border-border">
                SEO settings for this case study page. Leave blank to use
                default values.
              </p>
              <InputField
                label="SEO Title"
                name="title_name"
                placeholder="SEO page title"
                form={form}
                setField={setField}
              />
              <InputField
                label="Meta Description"
                name="description"
                placeholder="SEO meta description"
                textarea
                form={form}
                setField={setField}
              />
              <InputField
                label="Keywords"
                name="keywords"
                placeholder="SAP, ERP, Case Study, Implementation"
                form={form}
                setField={setField}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-muted-foreground">
                    Change Frequency
                  </label>
                  <select
                    value={form.changefreq}
                    onChange={(e) => setField("changefreq", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                  >
                    {[
                      "always",
                      "hourly",
                      "daily",
                      "weekly",
                      "monthly",
                      "yearly",
                      "never",
                    ].map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                </div>
                <InputField
                  label="Priority (0.0–1.0)"
                  name="priority_screen"
                  placeholder="0.8"
                  form={form}
                  setField={setField}
                />
              </div>
            </>
          )}

          {/* ── Tab Navigation + Bottom Save ── */}
          <div className="flex justify-between items-center pt-2 border-t border-border">
            <div className="flex gap-2">
              {activeTab !== "basic" && (
                <button
                  onClick={() =>
                    setActiveTab(TABS[TABS.indexOf(activeTab) - 1])
                  }
                  className="px-3 py-2 rounded-lg text-xs font-semibold border border-border text-foreground hover:bg-muted"
                >
                  ← Back
                </button>
              )}
              {activeTab !== "seo" && (
                <button
                  onClick={() =>
                    setActiveTab(TABS[TABS.indexOf(activeTab) + 1])
                  }
                  className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold border border-border text-foreground hover:bg-muted"
                >
                  Next <ChevronRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onBack}
                className="px-4 py-2 rounded-lg text-sm font-semibold border border-border text-foreground hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                style={{ background: "hsl(var(--accent))" }}
              >
                {loading ? "Saving..." : form.p_id > 0 ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
